import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './About.css';
import aboutHeroImg from '../../assets/About/building.jpg';

const About = () => {
  // 1. All 7 Core Values
  const coreValues = [
    {
      no: "01",
      title: "Integrity",
      desc: "Uncompromising ethical standards in every assessment, audit, and institutional advisory engagement.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      )
    },
    {
      no: "02",
      title: "Professionalism",
      desc: "Methodical execution delivered by experienced medical education advisors and institutional planners.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      )
    },
    {
      no: "03",
      title: "Confidentiality",
      desc: "Strict data privacy protocols safeguarding institutional blueprints, promoter strategies, and faculty profiles.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      )
    },
    {
      no: "04",
      title: "Transparency",
      desc: "Clear roadmaps, candid gap assessments, and milestone-based coordination across every project stage.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      )
    },
    {
      no: "05",
      title: "Institutional Focus",
      desc: "Aligning teaching hospitals, academic infrastructure, and manpower into one synchronized ecosystem.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
        </svg>
      )
    },
    {
      no: "06",
      title: "Structured Execution",
      desc: "Defined delivery frameworks, rigorous documentation coordination, and systematic readiness roadmaps.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="9 11 12 14 22 4"/>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      )
    },
    {
      no: "07",
      title: "Long-Term Relationships",
      desc: "Committed professional partnership from greenfield establishment to postgraduate expansion and beyond.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      )
    }
  ];

  // 2. All 6 Core Services
  const services = [
    { title: "New Medical College Establishment", link: "/services/new-medical-college-establishment" },
    { title: "MBBS Seat Expansion Advisory", link: "/services/mbbs-seat-expansion" },
    { title: "PG (MD/MS) Course & Seat Expansion", link: "/services/pg-course-seat-expansion" },
    { title: "Institutional & Teaching Hospital Compliance", link: "/services/institutional-compliance" },
    { title: "Documentation & Institutional Readiness", link: "/services/documentation-readiness" },
    { title: "Permanent & Full-Time Faculty Recruitment", link: "/services/medical-faculty-recruitment" }
  ];

  // 3. Why Nutrivio Differentiators
  const whyNutrivio = [
    {
      no: "01",
      title: "Integrated Institutional Perspective",
      desc: "Medical College development is treated as one cohesive ecosystem: academics, Teaching Hospital, infrastructure, manpower, clinical material, and documentation."
    },
    {
      no: "02",
      title: "Establishment to Expansion",
      desc: "Our end-to-end framework supports institutions from greenfield New Medical College planning through MBBS and PG academic seat expansions."
    },
    {
      no: "03",
      title: "Advisory + Faculty Capability",
      desc: "Strategic institutional advisory is complemented by genuine Permanent & Full-Time Medical Faculty Recruitment across all departments."
    },
    {
      no: "04",
      title: "Structured Professional Approach",
      desc: "Requirement assessment, planning, documentation, gap identification, and readiness coordination are handled within a defined professional framework."
    },
    {
      no: "05",
      title: "Institution-Specific Engagement",
      desc: "Every advisory engagement is customized according to the institution's actual development stage, existing capabilities, and agreed scope of work."
    },
    {
      no: "06",
      title: "Professional & Transparent Coordination",
      desc: "Structured communication, high confidentiality, meticulous documentation, and transparent coordination across all institutional touchpoints."
    }
  ];

  // 4. Exact Who We Serve
  const whoWeServe = [
    { title: "Educational Organisations", desc: "Institutions expanding their higher education portfolio into accredited medical education." },
    { title: "Healthcare Groups", desc: "Hospital chains planning to establish dedicated academic colleges and teaching hospital wings." },
    { title: "Trusts & Societies", desc: "Philanthropic, private, and social trusts establishing greenfield medical campuses." },
    { title: "Universities", desc: "State, private, and deemed universities setting up or expanding dedicated faculties of medicine." },
    { title: "Hospital Groups", desc: "Operational clinical facilities upgrading clinical infrastructure into teaching hospitals." },
    { title: "Promoters Planning New Medical Colleges", desc: "Visionary promoters requiring end-to-end feasibility, planning, and statutory readiness advisory." },
    { title: "Existing Medical Colleges Planning Academic Expansion", desc: "Medical colleges upgrading undergraduate intake capacity to 150, 200, or 250 MBBS seats." },
    { title: "Medical Colleges Planning PG Programmes", desc: "Institutions introducing new MD/MS clinical, para-clinical, and pre-clinical postgraduate specialities." },
    { title: "Teaching Hospitals Strengthening Institutional Readiness", desc: "Institutions conducting clinical material, bed occupancy, ICU, and statutory documentation audits." }
  ];

  return (
    <>
      <Helmet>
        <title>About Nutrivio | Medical College Advisory & Institutional Services</title>
        <meta
          name="description"
          content="Learn about Nutrivio, a medical education advisory company supporting new medical colleges, teaching hospitals, institutional planning, and faculty recruitment across India."
        />
      </Helmet>

      <div className="about-nutrivio-wrapper">
      
      {/* ================= 1. HERO SECTION ================= */}
      <section className="about-nutrivio-hero">
        <div className="about-nutrivio-container">
          
          <div className="about-nutrivio-hero-header">
            <div className="about-nutrivio-eyebrow center">
              <span className="about-nutrivio-eyebrow-dash"></span>
              <span>Medical College Establishment &amp; Institutional Advisory</span>
              <span className="about-nutrivio-eyebrow-dash"></span>
            </div>
            
            <h1 className="about-nutrivio-hero-title">
              About <span className="about-nutrivio-gradient-text">Nutrivio</span>
            </h1>
            
            <p className="about-nutrivio-hero-subtitle">
              Specialised Medical Education Institutional Advisory &amp; Professional Services Company supporting organisations, Medical Colleges and Teaching Hospitals across India.
            </p>
          </div>

          {/* Hero Banner Image Box with Floating Badges */}
          <div className="about-nutrivio-hero-image-box">
            <img 
              src={aboutHeroImg}
              alt="Medical College Infrastructure & Hospital" 
              className="about-nutrivio-hero-img"
            />
            <div className="about-nutrivio-hero-image-overlay">
              <div className="about-nutrivio-floating-chip">
                <span className="chip-badge">Focus</span>
                <div>
                  <strong>From Concept to Readiness</strong>
                  <small>Strategic Advisory &amp; Institutional Planning</small>
                </div>
              </div>
              <div className="about-nutrivio-floating-chip right">
                <span className="chip-badge green">Coverage</span>
                <div>
                  <strong>Pan-India Institutional Reach</strong>
                  <small>Promoters, Trusts, Universities &amp; Hospitals</small>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ================= 2. CORPORATE POSITIONING & SERVICE MATRIX ================= */}
      <section className="about-nutrivio-details">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-details-grid">
            
            {/* Left Narrative Column */}
            <div className="about-nutrivio-details-left">
              <div className="about-nutrivio-eyebrow">
                <span className="about-nutrivio-eyebrow-dash"></span>
                <span>Strategic Advisory Partner</span>
                <span className="about-nutrivio-eyebrow-dash"></span>
              </div>
              <h2 className="about-nutrivio-main-heading">
                Supporting the Development of Medical Education Institutions
              </h2>
              
              <p className="about-nutrivio-lead-para">
                Nutrivio Medical Services Private Limited is a specialised Medical Education Institutional Advisory &amp; Professional Services Company supporting organisations, Medical Colleges and Teaching Hospitals with institutional establishment, academic expansion, documentation, readiness and medical faculty requirements.
              </p>
              
              <p className="about-nutrivio-body-para">
                We aim to work alongside institutions as a professional support partner by understanding their requirements, identifying institutional gaps and providing structured planning, documentation and coordination support across academic, clinical, and manpower dimensions.
              </p>

              {/* Metrics Strip */}
              <div className="about-nutrivio-metrics-strip">
                <div className="about-nutrivio-metric-unit">
                  <h4>End-to-End</h4>
                  <span>Setup Advisory</span>
                </div>
                <div className="about-nutrivio-metric-sep"></div>
                <div className="about-nutrivio-metric-unit">
                  <h4>Permanent</h4>
                  <span>Faculty Solutions</span>
                </div>
                <div className="about-nutrivio-metric-sep"></div>
                <div className="about-nutrivio-metric-unit">
                  <h4>Pan-India</h4>
                  <span>Advisory Scope</span>
                </div>
              </div>
            </div>

            {/* Right Service Framework Card */}
            <div className="about-nutrivio-details-right">
              <div className="about-nutrivio-matrix-box">
                <div className="about-nutrivio-matrix-header">
                  <div>
                    <h6>Integrated Service Framework</h6>
                    <small>Core Institutional Capabilities</small>
                  </div>
                  <span className="about-nutrivio-live-tag">Scope of Support</span>
                </div>

                <div className="about-nutrivio-matrix-list">
                  {services.map((srv, idx) => (
                    <Link key={idx} to={srv.link} className="about-nutrivio-matrix-item">
                      <div className="about-nutrivio-index-badge">0{idx + 1}</div>
                      <span className="about-nutrivio-item-name">{srv.title}</span>
                      <span className="about-nutrivio-matrix-arrow">→</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= 3. VISION & MISSION ================= */}
      <section className="about-nutrivio-vm-section">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-vm-split-wrapper">
            
            {/* Vision Card */}
            <div className="about-nutrivio-vm-card vision-card">
              <div className="about-nutrivio-vm-hover-bar teal"></div>
              <div className="about-nutrivio-vm-icon-bubble teal">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              <span className="about-nutrivio-vm-type">Our Vision</span>
              <h3 className="about-nutrivio-vm-headline">Strengthening the Future of Medical Education</h3>
              <p className="about-nutrivio-vm-body">
                To contribute to the development and strengthening of medical education institutions through structured professional advisory, institutional planning and sustainable support solutions.
              </p>
            </div>

            {/* Mission Card */}
            <div className="about-nutrivio-vm-card mission-card">
              <div className="about-nutrivio-vm-hover-bar green"></div>
              <div className="about-nutrivio-vm-icon-bubble green">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <span className="about-nutrivio-vm-type">Our Mission</span>
              <h3 className="about-nutrivio-vm-headline">Institution-Focused Advisory &amp; Support</h3>
              <p className="about-nutrivio-vm-body">
                To provide Medical Colleges and Teaching Hospitals with integrated professional support across institutional establishment, academic expansion, documentation, readiness and medical faculty requirements through a structured, transparent and institution-focused approach.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ================= 4. WHY NUTRIVIO (6 DIFFERENTIATORS) ================= */}
      <section className="about-nutrivio-why-section">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-header-center">
            <div className="about-nutrivio-eyebrow center">
              <span className="about-nutrivio-eyebrow-dash"></span>
              <span>Institutional Advantage</span>
              <span className="about-nutrivio-eyebrow-dash"></span>
            </div>
            <h2 className="about-nutrivio-section-heading">Why Nutrivio?</h2>
            <p className="about-nutrivio-sub-lead">
              A structured, high-calibre advisory framework designed specifically for medical colleges and teaching hospitals.
            </p>
          </div>

          <div className="about-nutrivio-why-grid">
            {whyNutrivio.map((item, idx) => (
              <div key={idx} className="about-nutrivio-why-card">
                <div className="about-nutrivio-why-hover-bar"></div>
                <div className="about-nutrivio-why-card-top">
                  <span className="about-nutrivio-why-num">{item.no}</span>
                  <span className="about-nutrivio-why-tag">Advantage</span>
                </div>
                <h4 className="about-nutrivio-why-title">{item.title}</h4>
                <p className="about-nutrivio-why-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 5. OUR CORE VALUES (7 ITEMS) ================= */}
      <section className="about-nutrivio-values-section">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-header-center">
            <div className="about-nutrivio-eyebrow center">
              <span className="about-nutrivio-eyebrow-dash"></span>
              <span>Guiding Principles</span>
              <span className="about-nutrivio-eyebrow-dash"></span>
            </div>
            <h2 className="about-nutrivio-section-heading">Our Core Values</h2>
            <p className="about-nutrivio-sub-lead">
              The foundational principles governing our assessments, advisory roadmaps, and long-term institutional partnerships.
            </p>
          </div>

          <div className="about-nutrivio-values-grid">
            {coreValues.map((val, idx) => (
              <div key={idx} className="about-nutrivio-val-box">
                <div className="about-nutrivio-val-hover-bar"></div>
                <div className="about-nutrivio-val-top">
                  <div className="about-nutrivio-val-icon">{val.icon}</div>
                  <span className="about-nutrivio-val-num">{val.no}</span>
                </div>
                <h4 className="about-nutrivio-val-title">{val.title}</h4>
                <p className="about-nutrivio-val-desc">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 6. WHO WE SERVE (9 SEGMENTS) ================= */}
      <section className="about-nutrivio-serve-section">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-header-center">
            <div className="about-nutrivio-eyebrow center">
              <span className="about-nutrivio-eyebrow-dash"></span>
              <span>Target Ecosystem</span>
              <span className="about-nutrivio-eyebrow-dash"></span>
            </div>
            <h2 className="about-nutrivio-section-heading">Who We Serve</h2>
            <p className="about-nutrivio-sub-lead">
              Professional advisory and institutional solutions engineered for leadership teams, promoters, and medical education trusts.
            </p>
          </div>

          <div className="about-nutrivio-serve-grid">
            {whoWeServe.map((item, idx) => (
              <div key={idx} className="about-nutrivio-serve-card">
                <div className="about-nutrivio-serve-check">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div className="about-nutrivio-serve-info">
                  <h4 className="about-nutrivio-serve-title">{item.title}</h4>
                  <p className="about-nutrivio-serve-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 7. BOTTOM CALL TO ACTION ================= */}
      <section className="about-nutrivio-cta-section">
        <div className="about-nutrivio-container">
          <div className="about-nutrivio-cta-box">
            
            {/* Ambient Radial Accent */}
            <div className="about-nutrivio-cta-ambient-glow"></div>

            <div className="about-nutrivio-cta-pill">
              <span className="about-nutrivio-cta-dot"></span>
              <span>Partner with Nutrivio</span>
            </div>

            <h2>Ready to Build or Strengthen Your Medical Institution?</h2>
            <p>
              Whether you are planning a new medical college, expanding MBBS or PG seats, strengthening compliance readiness, or hiring full-time medical faculty, Nutrivio is ready to support your institutional journey.
            </p>

            <div className="about-nutrivio-cta-actions">
              <Link to="/contact" className="about-nutrivio-btn-primary">
                <span>Discuss Your Project</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              
              <Link to="/for-medical-faculty" className="about-nutrivio-btn-secondary">
                Request Medical Faculty
              </Link>
            </div>

            {/* Brand Closing Note */}
            <div className="about-nutrivio-closing-strip">
              <span>NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED &bull; Strategic Institutional Advisory across India</span>
            </div>

          </div>
        </div>
      </section>

    </div>
    </>
  );
};

export default About;